/*
 * Timond ETA engine — SKU-prefix driven delivery estimate (Path A, native).
 *
 * Source of truth for numbers: Google Sheet "Timond — Fulfillment SLA Matrix".
 * Numbers are mirrored into snippets/timond-eta-registry.liquid.
 *
 * Contract:
 *   - Key = first 3 chars of variant.sku, uppercased.
 *   - Renders a date range ONLY when the profile has every number it needs.
 *   - Anything missing/unknown/blocked -> neutral German fallback. Never invents a date.
 *   - Business days only: skips Sat/Sun and German federal holidays.
 *   - Cutoff: after cutoff (Europe/Berlin) the clock starts the next business day.
 */
(function () {
  'use strict';

  var MONTHS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                   'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

  function readRegistry() {
    var el = document.getElementById('timond-eta-registry');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* Current wall-clock date/time in Europe/Berlin, independent of visitor TZ. */
  function berlinNow() {
    var parts;
    try {
      parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Berlin',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false
      }).formatToParts(new Date());
    } catch (e) {
      var d = new Date();
      return { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate(),
               hh: d.getHours(), mm: d.getMinutes() };
    }
    var g = {};
    parts.forEach(function (p) { g[p.type] = p.value; });
    return {
      y: parseInt(g.year, 10), m: parseInt(g.month, 10), d: parseInt(g.day, 10),
      hh: parseInt(g.hour, 10) % 24, mm: parseInt(g.minute, 10)
    };
  }

  function iso(dt) {
    var m = String(dt.getUTCMonth() + 1).padStart(2, '0');
    var d = String(dt.getUTCDate()).padStart(2, '0');
    return dt.getUTCFullYear() + '-' + m + '-' + d;
  }

  function isBusinessDay(dt, holidaySet) {
    var wd = dt.getUTCDay();
    if (wd === 0 || wd === 6) return false;
    return !holidaySet[iso(dt)];
  }

  function addBusinessDays(start, n, holidaySet) {
    var dt = new Date(start.getTime());
    var added = 0;
    var guard = 0;
    while (added < n && guard < 400) {
      dt.setUTCDate(dt.getUTCDate() + 1);
      guard++;
      if (isBusinessDay(dt, holidaySet)) added++;
    }
    return dt;
  }

  /* First day production can actually start, honouring cutoff + non-business days. */
  function productionStart(now, cutoff, holidaySet) {
    var dt = new Date(Date.UTC(now.y, now.m - 1, now.d));
    var past = false;
    if (typeof cutoff === 'string' && /^\d{1,2}:\d{2}$/.test(cutoff)) {
      var bits = cutoff.split(':');
      var cm = parseInt(bits[0], 10) * 60 + parseInt(bits[1], 10);
      past = (now.hh * 60 + now.mm) >= cm;
    }
    if (past || !isBusinessDay(dt, holidaySet)) {
      dt = addBusinessDays(dt, 1, holidaySet);
    }
    return dt;
  }

  function fmt(dt, withYear) {
    var s = dt.getUTCDate() + '. ' + MONTHS_DE[dt.getUTCMonth()];
    return withYear ? (s + ' ' + dt.getUTCFullYear()) : s;
  }

  function countryName(code) {
    return { DE: 'Deutschland', AT: 'Österreich', CH: 'die Schweiz' }[code] || code;
  }

  /*
   * Resolve [minDays, maxDays] of all-in business days, or null when unknown.
   *   all_in : supplier target already includes production -> target .. target+buffer
   *   split  : production + transit are added
   */
  function resolveDays(profile, country) {
    if (!profile || !profile.mode || profile.mode === 'none') return null;
    var buffer = typeof profile.buffer === 'number' ? profile.buffer : 0;

    if (profile.mode === 'all_in') {
      var t = profile.target && profile.target[country];
      if (typeof t !== 'number' || t <= 0) return null;
      return [t, t + buffer];
    }

    if (profile.mode === 'split') {
      var p = profile.proc || {};
      var tr = (profile.transit || {})[country];
      if (typeof p.min !== 'number' || typeof p.max !== 'number') return null;
      if (!tr || typeof tr.min !== 'number' || typeof tr.max !== 'number') return null;
      return [p.min + tr.min, p.max + tr.max + buffer];
    }
    return null;
  }

  function skuPrefix(sku) {
    if (typeof sku !== 'string') return null;
    var s = sku.trim().toUpperCase();
    return s.length >= 3 ? s.slice(0, 3) : null;
  }

  function render(root, reg, sku) {
    if (!root || !reg) return;

    var country = (root.getAttribute('data-eta-country') || '').toUpperCase();
    var holidaySet = {};
    (reg.holidays || []).forEach(function (h) { holidaySet[h] = true; });

    var prefix = skuPrefix(sku);
    var profile = prefix ? (reg.profiles || {})[prefix] : null;
    var days = resolveDays(profile, country);

    var target = root.querySelector('[data-eta-text]');
    if (!target) return;

    if (!days) {
      root.setAttribute('data-eta-state', 'fallback');
      target.textContent = reg.fallback_text || 'Lieferzeit wird im Checkout bestätigt.';
      return;
    }

    var now = berlinNow();
    var start0 = productionStart(now, profile.cutoff, holidaySet);
    var a = addBusinessDays(start0, days[0], holidaySet);
    var b = addBusinessDays(start0, days[1], holidaySet);
    var sameYear = a.getUTCFullYear() === b.getUTCFullYear();

    root.setAttribute('data-eta-state', 'ok');
    target.innerHTML =
      'Voraussichtliche Lieferung nach ' + countryName(country) + ': <b>' +
      '<time datetime="' + iso(a) + '">' + fmt(a, !sameYear) + '</time>' +
      ' – ' +
      '<time datetime="' + iso(b) + '">' + fmt(b, true) + '</time>' +
      '</b>';
  }

  function currentSku(root) {
    var inline = root.getAttribute('data-eta-sku');
    if (inline) return inline;
    return null;
  }

  function boot() {
    var reg = readRegistry();
    if (!reg) return;
    var roots = document.querySelectorAll('[data-timond-eta]');
    if (!roots.length) return;

    Array.prototype.forEach.call(roots, function (root) {
      render(root, reg, currentSku(root));
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var v = event && event.data && event.data.variant;
        if (!v) return;
        Array.prototype.forEach.call(document.querySelectorAll('[data-timond-eta]'), function (root) {
          root.setAttribute('data-eta-sku', v.sku || '');
          render(root, reg, v.sku);
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
